import { useCallback, useMemo, useRef, useEffect, useState } from 'react'
import { Row, Col, TreeSelect, Table, Select, Spin, Tabs } from 'antd'
import { TranslationKeys } from '../../../Constants/TranslationKeys'
import {
  BaseStorePricelist,
  DateFormatNoYear,
  VariantModelValue,
  listingsAllValue,
} from '../../../Constants/Configuration'
import {
  setPriceFilterDateRange,
  setPriceListingFilter,
  resetPriceChanged,
  setChannelType,
} from '../../../Slices/Kiosk/PriceSlice'
import {
  renderTranslation,
  handleMultipleTableScroll,
  getCurrencyConversion,
  handleMultipleTableHover,
  handleMultipleLeaveTableHover,
} from '../../../Helpers/GenericHelpers'
import {
  PriceDateRange,
  PriceType,
  PriceTypeShow,
  priceStandardHeight,
  priceStandardRowHeight,
} from '../../../Constants/TableConfiguration'
import { scrollToTableRow } from '../../../Helpers/TableHelper'
import PricesHeader from './PricesHeader'
import { useAppDispatch, useAppSelector } from '../../../hooks'
import {
  getListingBySize,
  getPriceDataByType,
  getMainPriceTableData,
  retrieveListingList,
  getPriceSellAndPurchase,
  getPriceSellAndPurchaseForDate,
  getRowInnerElementsCount,
} from '../../../Helpers/PricesHelper'
import dayjs from 'dayjs'
import { retrieveSelectedArticleKey } from 'Helpers/ArticlesHelper'
import { handlePricesData } from 'Services/Kiosk/ArticleServices'
import DateTimeRangeFilter from 'Components/Generic/DateTimeFilter'
import { TypeOfChannel, iconDictionary } from 'Slices/Omnichannel/DiscountSlice'
import PricesTable from '../../../Components/Table/Prices'
import { checkDefaultTypeOfChannel } from 'Helpers/ConfigurationHelper'
import { isValidDate, localeSpecificDate } from 'Helpers/DateHelpers'

function Prices(props) {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const priceData: [] = useAppSelector(state => state.present.articleDetail.articleSelected.priceData)
  const priceListingFilter = useAppSelector(state => state.present.price.priceListingFilter)
  const priceTypeFilter = useAppSelector(state => state.present.price.priceTypeFilter)
  const priceTypeShow = useAppSelector(state => state.present.price.priceTypeShow)
  const articleSelected = useAppSelector(state => state.present.articleDetail.articleSelected)
  const workspacePricesVisible = useAppSelector(state => state.present.configuration.workspacePricesVisible)
  const channelType: TypeOfChannel = useAppSelector(state => state.present.price.channelType)
  const sites = useAppSelector(state => state.present.configuration.sites)
  const stores = useAppSelector(state => state.present.configuration.stores)
  const marketplace = useAppSelector(state => state.present.configuration.marketplace)
  const isSingleTab = useAppSelector(state => state.present.configuration.isPricesSingleTab)

  const filterDateRange = useAppSelector(state => state.present.price.priceFilterDateRange)
  const rowKeysSelected = useAppSelector(state => state.present.article.rowKeysSelected)
  const publicWorkspace = useAppSelector(state => state.present.configuration.publicWorkspace)
  const isOmnichannelAndPricesVisibile = useAppSelector(
    state => state.present.configuration.isOmnichannelAndPricesVisibile,
  )
  const articleKey: any = retrieveSelectedArticleKey(rowKeysSelected)

  const [loadingPrices, setLoadingPrices] = useState(false)
  const [datePickerValueFrom, setDatePickerValueFrom] = useState(undefined)
  const [datePickerValueTo, setDatePickerValueTo] = useState(undefined)

  const filterDatesOptions = [
    {
      label: renderTranslation(language, TranslationKeys.priceSectionTableFilterRangeMonth),
      value: PriceDateRange.month,
    },
    {
      label: renderTranslation(language, TranslationKeys.priceSectionTableFilterRange3Months),
      value: PriceDateRange.threeMonths,
    },
    {
      label: renderTranslation(language, TranslationKeys.priceSectionTableFilterRangeYear),
      value: PriceDateRange.year,
    },
    {
      label: renderTranslation(language, TranslationKeys.priceSectionTableFilterRangeFreeDates),
      value: PriceDateRange.freeDates,
    },
  ]

  const isArticleSelected = articleSelected.fetchedPriceData
  const priceTableRef = useRef<any>(null)

  const hasRules = useMemo(
    () => priceData.findIndex((pd: any) => pd.prices.findIndex((p: any) => p.ruleId && p.ruleId !== 0) >= 0) >= 0,
    [priceData],
  )
  const listingAll = useMemo(() => {
    return {
      value: listingsAllValue,
      label: renderTranslation(language, TranslationKeys.priceSectionDropdownListingAllValue),
    }
  }, [language])

  const [listingsList, isBySize] = useMemo(() => {
    return [retrieveListingList(priceData), getListingBySize(priceData)]
  }, [priceData])

  const priceDataFiltered = useMemo(() => {
    return getPriceDataByType(priceData, priceTypeFilter)
  }, [priceData, priceTypeFilter, filterDateRange])

  const listingsSelected = useMemo(() => {
    const listingValues = priceListingFilter.map(x => x.value)
    return retrieveListingList(priceDataFiltered).filter(x =>
      listingValues.indexOf(listingsAllValue) !== -1
        ? listingValues.indexOf(listingsAllValue) !== -1
        : listingValues.indexOf(x) !== -1,
    )
  }, [priceDataFiltered, priceListingFilter])

  const listingsSelectList = useMemo(() => {
    const listings =
      listingsList
        ?.filter(x => x !== VariantModelValue)
        .map(x => {
          return x == BaseStorePricelist
            ? { value: x, label: renderTranslation(language, TranslationKeys.pricelistBaseStore) }
            : { value: x, label: x }
        }) ?? []

    listings.unshift(listingAll)

    return listings
  }, [listingsList, listingAll])

  const dateOnChange = (date: any, from: boolean) => {
    const dateToSave = dayjs(date).utc()
    dateToSave.set('hours', 0).set('minutes', 0).set('seconds', 0).set('milliseconds', 0)
    from
      ? dispatch(setPriceFilterDateRange({ dateFrom: date, dateTo: filterDateRange['dateTo'] }))
      : dispatch(setPriceFilterDateRange({ dateFrom: filterDateRange['dateFrom'], dateTo: date }))
    from ? setDatePickerValueFrom(date) : setDatePickerValueTo(date)
  }

  const priceTableColumns = useMemo(() => {
    return [
      {
        title: !isBySize ? (
          <>
            {priceTypeShow === PriceTypeShow.omni ? (
              <PricesHeader
                priceTypeFilter={priceTypeFilter}
                priceListingFilterPurchase={PriceType.purchase}
                priceListingFilterSell={PriceType.sell}
                sellAndPurchaseValue={getPriceSellAndPurchase(priceData, null)}
                isBySize={isBySize}
                size={null}
                priceTypeShow={priceTypeShow}
              />
            ) : null}
          </>
        ) : (
          <>
            {priceTypeShow !== PriceTypeShow.history ? (
              <>
                <div className='listing-start-date-col-head'></div>
                <PricesHeader
                  priceTypeFilter={priceTypeFilter}
                  priceListingFilterPurchase={PriceType.purchase}
                  priceListingFilterSell={PriceType.sell}
                  sellAndPurchaseValue={getPriceSellAndPurchase(priceData, null)}
                  isBySize={isBySize}
                />
              </>
            ) : null}
          </>
        ),
        dataIndex: isBySize ? 'startDate' : 'fakeIndex',
        key: 'startDateColumn',
        width: 100,
        className: 'listing-start-date',
        render: (dateTime, record) => {
          const sellAndPurchaseValueForDate = getPriceSellAndPurchaseForDate(
            priceData,
            record.startDate,
            record.size ?? null,
          )
          const rowsCount = getRowInnerElementsCount(priceData, record.startDate)
          const startDateAbbr =
            record.startDate && isValidDate(localeSpecificDate(record.startDate))
              ? dayjs(localeSpecificDate(record.startDate)).format(DateFormatNoYear)
              : null
          const endDateAbbr =
            record.endDate && isValidDate(localeSpecificDate(record.endDate))
              ? dayjs(localeSpecificDate(record.endDate)).format(DateFormatNoYear)
              : null
          return (
            <Row
              style={{
                height: existsSizePrices && record.size == null ? 1 : priceStandardRowHeight * rowsCount,
              }}
            >
              {isBySize && !record.size && priceTypeShow === PriceTypeShow.history ? (
                <PricesHeader
                  priceTypeFilter={priceTypeFilter}
                  priceListingFilterPurchase={PriceType.purchase}
                  priceListingFilterSell={PriceType.sell}
                  sellAndPurchaseValue={getPriceSellAndPurchase(priceData, null)}
                  isBySize={isBySize}
                  priceTypeShow={priceTypeShow}
                />
              ) : null}
              {isBySize && record.size && priceTypeShow !== PriceTypeShow.history ? (
                <>
                  <Col span={8} className='sale'>
                    {getCurrencyConversion(sellAndPurchaseValueForDate?.buyPrice)}
                  </Col>
                  <Col span={8} className=''>
                    {getCurrencyConversion(sellAndPurchaseValueForDate?.sellPrice)}
                  </Col>
                  <Col span={8} className={(record.isCurrent ? ' bold' : '') + (!dateTime ? ' v.hidden' : '')}>
                    {record.size}
                  </Col>
                </>
              ) : null}
              {priceTypeShow === PriceTypeShow.history && record.size && isBySize ? (
                <>
                  <Col span={8} className='sale'>
                    {getCurrencyConversion(sellAndPurchaseValueForDate?.buyPrice)}
                  </Col>
                  <Col span={8} className=''>
                    {getCurrencyConversion(sellAndPurchaseValueForDate?.sellPrice)}
                  </Col>
                  <Col span={8} className={(record.isCurrent ? ' bold' : '') + (!dateTime ? ' v.hidden' : '')}>
                    {record.size}
                  </Col>
                </>
              ) : null}
              {priceTypeShow === PriceTypeShow.history && !isBySize ? (
                <>
                  <Col span={5} className='sale text-center'>
                    {getCurrencyConversion(sellAndPurchaseValueForDate?.buyPrice)}
                  </Col>
                  <Col span={5} className='text-center'>
                    {getCurrencyConversion(sellAndPurchaseValueForDate?.sellPrice)}
                  </Col>
                </>
              ) : null}
              <Col
                span={priceTypeShow === PriceTypeShow.history ? (isBySize ? 8 : 14) : 24}
                className={priceTypeShow === PriceTypeShow.history && !isBySize ? 'pl-2' : ''}
              >
                <div style={{ display: 'flex', flexWrap: 'nowrap', fontSize: 'smaller' }}>
                  <div style={{ textAlign: 'left', width: '50%', fontSize: 'smaller', fontStyle: 'italic' }}>
                    {record.ruleId && !(record.size && isBySize) ? (
                      <>
                        <div style={{ textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                          {record.ruleName}
                        </div>
                        {startDateAbbr ? (
                          <div>{`(${startDateAbbr}${endDateAbbr ? `-${endDateAbbr}` : '.'})`}</div>
                        ) : null}
                      </>
                    ) : null}
                  </div>
                  <div
                    className={
                      (record.isCurrent ? ' bold' : '') +
                      (!dateTime ? ' v.hidden' : '') +
                      (priceTypeShow === PriceTypeShow.history && isBySize ? ' date-size' : '')
                    }
                    style={{ textAlign: 'right', width: '50%' }}
                  >
                    {!record.size ? record.startDate : null}
                  </div>
                </div>
              </Col>
            </Row>
          )
        },
      },
    ]
  }, [isBySize, priceData, priceTypeFilter, priceTypeShow, filterDateRange])

  const distinctPriceDates = useMemo(() => {
    return getMainPriceTableData(priceDataFiltered, isBySize)
  }, [isBySize, priceDataFiltered])

  const existsSizePrices = useMemo(
    () => distinctPriceDates && distinctPriceDates.findIndex(x => x.size && x.size !== '') >= 0,
    [distinctPriceDates],
  )

  const onListingSelect = useCallback(
    listingValues => {
      if (
        (listingValues.find(x => x.value === listingsAllValue) &&
          !priceListingFilter.find(x => x.value === listingsAllValue)) ||
        listingValues.length === 0
      ) {
        dispatch(setPriceListingFilter([listingAll]))
      } else {
        const listingToSave = listingValues
          .filter(x => x.value !== listingsAllValue)
          .map(x => {
            return {
              value: x.value,
              label: x.value,
            }
          })

        dispatch(setPriceListingFilter(listingToSave))
      }
    },
    [priceListingFilter, dispatch, listingAll],
  )

  const disabledPickerDateTo = current => {
    // Can not select days before dateFrom
    return current && (current < datePickerValueFrom || current > dayjs())
  }

  const disabledPickerDateFrom = current => {
    return current && (current > datePickerValueTo || current > dayjs())
  }

  useEffect(() => {
    if (isOmnichannelAndPricesVisibile) {
      const typeOfChannel = checkDefaultTypeOfChannel(publicWorkspace).toString()
      dispatch(setChannelType(parseInt(typeOfChannel)))
    }
  }, [])

  useEffect(() => {
    const priceMainTableBody =
      priceTableRef && priceTableRef.current && priceTableRef.current.querySelector('.ant-table-body')
    priceMainTableBody && priceMainTableBody.addEventListener('scroll', handleMultipleTableScroll, { passive: true })

    const rows = priceMainTableBody && priceMainTableBody.querySelectorAll('tr.ant-table-row')

    let i = 0
    rows &&
      rows.forEach(row => {
        row.classList.add(`sameRow-${i}`)
        i++
        row.addEventListener('mouseover', handleMultipleTableHover)
        row.addEventListener('mouseleave', handleMultipleLeaveTableHover)
      })

    const activeRule = distinctPriceDates.find(x => x.isCurrent && !x.size)

    if (activeRule) {
      scrollToTableRow('' + activeRule.startDate + activeRule.listings.join('-') + '')
    }

    return () => {
      priceMainTableBody && priceMainTableBody.removeEventListener('scroll', handleMultipleTableScroll)
      rows &&
        rows.forEach(row => {
          row.removeEventListener('mouseover', handleMultipleTableHover)
          row.removeEventListener('mouseleave', handleMultipleLeaveTableHover)
        })
    }
  }, [distinctPriceDates, isArticleSelected])

  useEffect(() => {
    dispatch(setPriceListingFilter([listingAll]))
  }, [isArticleSelected])

  useEffect(() => {
    dispatch(resetPriceChanged())
    if (!workspacePricesVisible || !articleKey) return
    if (priceTypeShow === PriceTypeShow.history && (!filterDateRange.dateFrom || !filterDateRange.dateTo)) return
    setLoadingPrices(true)
    handlePricesData(articleKey, dispatch, () => setLoadingPrices(false))()
  }, [priceTypeShow, filterDateRange, articleKey, channelType, dispatch, workspacePricesVisible])

  const handleContent = useMemo(() => {
    const result = Object.values(TypeOfChannel)
      .filter(v => !isNaN(Number(v)))
      .filter(x => (!sites ? x !== TypeOfChannel.Site : true))
      .filter(x => (!stores ? x !== TypeOfChannel.Store : true))
      .filter(x => (!marketplace ? x !== TypeOfChannel.Marketplace : true))
      .map(value => {
        const Icon = iconDictionary[value]
        const id = String(value)
        return {
          label: (
            <>
              <div className='icon-container-tabs'>
                <Icon className={'prices-tab-icon'} />
              </div>
              <div className='discount-type-tab-label'>
                {renderTranslation(language, TranslationKeys['channelType' + TypeOfChannel[id]])}
              </div>
            </>
          ),
          key: id,
        }
      })
    return result
  }, [language, sites, stores, marketplace])

  return workspacePricesVisible ? (
    <>
      <Row className='overflow-hidden custom-scroll flex-nowrap' justify={'start'}>
        <Col span={4} className=' pt-4 prices-history-box ' id='discount-type-tabs'>
          {!isSingleTab ? (
            <Tabs
              defaultActiveKey={channelType.toString()}
              activeKey={channelType.toString()}
              animated
              onChange={e => {
                dispatch(setChannelType(parseInt(e)))
              }}
              items={handleContent}
            />
          ) : null}
        </Col>
        <Col>
          <div className='upper-label pl-2 pt-2'>
            {renderTranslation(language, TranslationKeys.priceSectionDropdownListingLabel)}
          </div>
          <TreeSelect
            treeData={listingsSelectList}
            treeCheckable={true}
            treeCheckStrictly={true}
            onChange={onListingSelect}
            value={
              priceListingFilter.length === 0
                ? [
                    {
                      value: listingsAllValue,
                      label: renderTranslation(language, TranslationKeys.priceSectionDropdownListingAllValue),
                    },
                  ]
                : priceListingFilter
            }
            disabled={false}
          />
        </Col>
        {isArticleSelected && priceTypeShow === PriceTypeShow.history ? (
          <Col className='pl-2'>
            <div className='upper-label pt-2'>&nbsp;</div>
            <Select
              size='small'
              value={props.filterDatesSelected}
              onChange={props.handleRangeSelectChange}
              className='prices-history w-180-p d-flex-c ml-0 mr-0'
              options={filterDatesOptions}
            />
          </Col>
        ) : null}
      </Row>
      <Row className='overflow-hidden custom-scroll flex-nowrap' ref={priceTableRef}>
        <Col span={6} className='pl-2 pr-2 mb-2 pt-2 prices-history-box'>
          {isArticleSelected ? (
            <>
              {priceTypeShow === PriceTypeShow.history ? (
                <Col>
                  {props.filterDatesSelected === PriceDateRange.freeDates ? (
                    <DateTimeRangeFilter
                      className='prices-history d-flex-c'
                      inColumn={false}
                      sizeComponent='small'
                      setDateFrom={datePickerValueFrom}
                      setDateTo={datePickerValueTo}
                      onDateFromChange={e => {
                        dateOnChange(e, true)
                      }}
                      onDateToChange={e => {
                        dateOnChange(e, false)
                      }}
                      setDisableDateFrom={disabledPickerDateFrom}
                      setDisableDateTo={disabledPickerDateTo}
                    />
                  ) : (
                    <div className='empty-space'></div>
                  )}
                  <div className='empty-space'></div>
                </Col>
              ) : null}
              <Spin className='max-h-100' spinning={loadingPrices} size='large'>
                <Table
                  id='price-main-table'
                  rowKey={record => {
                    return record.startDate + (record.size ? '-' + record.size : '')
                  }}
                  className='same-table-scroll'
                  columns={priceTableColumns}
                  dataSource={hasRules ? distinctPriceDates : null}
                  pagination={false}
                  tableLayout='fixed'
                  scroll={{ y: priceStandardHeight }}
                />
              </Spin>
            </>
          ) : null}
        </Col>
        {hasRules ? (
          <Col span={18} id='price-table-box' className='pl-2 pr-2 mb-2 scroll-overflow-price-table'>
            <div className='multiple-table-container d-flex pt-2'>
              {/* first child void */}
              <div></div>
              {listingsSelected && listingsSelected.length > 0 && isArticleSelected
                ? listingsSelected
                    .filter(x => x !== VariantModelValue)
                    .map(listing => {
                      return (
                        <PricesTable
                          key={'listing-table-' + listing}
                          activeListing={listing}
                          distinctPriceDates={distinctPriceDates}
                          priceTypeShow={priceTypeShow}
                        />
                      )
                    })
                : null}
            </div>
          </Col>
        ) : null}
      </Row>
    </>
  ) : null
}

export default Prices
